/**
 * `fromTick` and `toParts` (1.K-1 deliverable 6). `toParts` gives the parts, at a precision,
 * whose unit contains the tick (minute precision is 1.L-2's `present_parts`): always with the key
 * of the tick's own era, every `year` that era's year number by the anchor formula, extrapolated
 * when the unit began before the era did.
 */
import { isTick, PRECISIONS, } from '@codex/contracts';
import { moonsAt, seasonAt, weekdayAt } from './cycles.js';
import { eraAt, eraYearOf } from './eras.js';
import { floorDiv, mul } from './math.js';
import { isCompiled } from './types.js';
import { locate, monthLength } from './year.js';
export function fromTick(calendar, tick) {
    if (!isCompiled(calendar))
        return { ok: false, reason: 'calendar_invalid' };
    const at = isTick(tick) ? locate(calendar, tick) : null;
    if (at === null)
        return { ok: false, reason: 'out_of_tick_range' };
    const era = eraAt(calendar, tick);
    const eraYear = era === undefined ? Number.NaN : eraYearOf(era, at.year);
    const sph = calendar.secondsPerHour;
    const spm = calendar.secondsPerMinute;
    return {
        ok: true,
        date: {
            absoluteYear: at.year,
            era: era !== undefined && Number.isSafeInteger(eraYear) ? { key: era.key, year: eraYear } : null,
            month: at.month.intercalary ? null : at.month.key,
            intercalary: at.month.intercalary ? at.month.key : null,
            day: at.day,
            dayOfYear: at.dayOfYear,
            hour: Math.floor(at.second / sph),
            minute: Math.floor((at.second % sph) / spm),
            second: at.second % spm,
            weekday: weekdayAt(calendar, at),
            season: seasonAt(calendar, at),
            moons: moonsAt(calendar, at),
        },
    };
}
/**
 * The parts at `precision` whose unit contains `tick`: the year-week at `week`; at `day` in an
 * intercalary month, `{era, year, intercalary}`, with `day` only when that month has more than one
 * day that year; at `month` in an intercalary month, `{era, year, intercalary}`; the first year of
 * the unit at `decade` and `century`; `{era}` at `era`. Never sets `part`. Null for a tick that no
 * era contains, at `season` in a calendar without seasons, and as deliverable 2 says.
 */
export function toParts(calendar, tick, precision) {
    if (!isCompiled(calendar) || !isTick(tick) || !PRECISIONS.includes(precision))
        return null;
    const era = eraAt(calendar, tick);
    const at = era === undefined ? null : locate(calendar, tick);
    if (era === undefined || at === null)
        return null;
    if (precision === 'era')
        return { era: era.key };
    let parts;
    const year = eraYearOf(era, at.year);
    if (precision === 'year' || precision === 'decade' || precision === 'century') {
        const size = precision === 'year' ? 1 : precision === 'decade' ? 10 : 100;
        parts = { era: era.key, year: mul(floorDiv(year, size), size) };
    }
    else if (precision === 'season') {
        const season = seasonAt(calendar, at);
        if (season === null)
            return null;
        parts = { era: era.key, year: eraYearOf(era, season.absoluteYear), season: season.key };
    }
    else if (precision === 'week') {
        parts = { era: era.key, year, week: floorDiv(at.dayOfYear, calendar.weekdays.length) + 1 };
    }
    else {
        const month = at.month;
        if (!month.intercalary) {
            parts = { era: era.key, year, month: month.key };
            if (precision !== 'month')
                parts.day = at.day;
        }
        else {
            parts = { era: era.key, year, intercalary: month.key };
            if (precision !== 'month' && monthLength(month, at.leap) > 1)
                parts.day = at.day;
        }
        if (precision === 'hour' || precision === 'minute') {
            parts.hour = Math.floor(at.second / calendar.secondsPerHour);
        }
        if (precision === 'minute') {
            parts.minute = Math.floor((at.second % calendar.secondsPerHour) / calendar.secondsPerMinute);
        }
    }
    return Number.isSafeInteger(parts.year) ? parts : null;
}
//# sourceMappingURL=from-tick.js.map